import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';
// @ts-expect-error
import type {LocationShape} from 'react-router-dom';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import {getReadableDelay} from 'nutshell-core/date-time/delays';

import {ActionBox} from 'shells/action-box/action-box';
import {Button} from 'shells/button/button';
import {ButtonMover} from 'shells/button-mover/button-mover';
import {colors} from 'shells/colors/colors';
import {Dialog} from 'shells/dialog/dialog';
import {ErrorCircleIcon, WarningIcon, EmailSequenceIcon} from 'shells/icon';
import {Link} from 'shells/link/link';
import {SavedSettingToast} from 'shells/saved-setting-toast/saved-setting-toast';

import type {
    SessionUser as SessionUserFragment,
    LeadStagesEdge_unfiredEmailAutomations,
    LeadStagesEdge_emailAutomationStatuses,
} from 'nutshell-graphql-types';

import {getEmailAutomationStatusDescription} from './get-email-automation-status-description';
import type {Description} from './get-email-automation-status-description';

import styles from './automated-email-row.module.css';
// Our types can't guarantee we'll have an email list, or a name, but we should.  Fallback is unlikely.
const FALLBACK_NAME = 'Automated email';

type Props = {
    emailAutomationStatus: LeadStagesEdge_emailAutomationStatuses | null | undefined;
    emailAutomation: LeadStagesEdge_unfiredEmailAutomations;
    onStart: (id: string) => Promise<any>;
    onCancel: (id: string) => void;
    user: SessionUserFragment | null | undefined;
    onSendNextUnsentEmail?: (queuedEmailId: string) => Promise<any>;
    setCancelButtonRef: (arg1?: HTMLElement | null | undefined) => void;
};

// @ts-expect-error
function determineTitle(status, subscription): string {
    let title;
    if (status) {
        title = status.emailAutomation.emailList && status.emailAutomation.emailList.name;
    } else {
        title = subscription.emailList && subscription.emailList.name;
    }

    return title || FALLBACK_NAME;
}

function determineDescriptions(
    // @ts-expect-error
    status,
    // @ts-expect-error
    subscription,
    // @ts-expect-error
    currentUserId,
    // @ts-expect-error
    isSent,
    // @ts-expect-error
    isSending
): Description[] {
    if (isSent) {
        return [{description: 'Sent!'}];
    }

    if (isSending) {
        return [{description: 'Sending…'}];
    }

    if (status) {
        return getEmailAutomationStatusDescription(status, currentUserId);
    }
    const stageName = subscription.entersStage.name;
    const delay = subscription.delay
        ? {
              unit: subscription.delay.unit,
              timespan: subscription.delay.timespan,
          }
        : null;

    const description = delay
        ? `Will start ${getReadableDelay(delay)} after the lead enters ${stageName}`
        : `Will start after the lead enters ${stageName}`;

    return [{description}];
}

// @ts-expect-error
function determineSendNowDescription(status): string {
    // The number of messages remaing to be sent after sending the next
    const remainingMessagesCount =
        status && status.unsentMessageCount ? status.unsentMessageCount - 1 : undefined;

    if (remainingMessagesCount) {
        if (remainingMessagesCount > 1) {
            return `This will send one email now and send ${remainingMessagesCount} follow-ups later. Are you ready to send this now?`;
        }

        if (remainingMessagesCount === 1) {
            return 'This will send one email now and send 1 follow-up later. Are you ready to send this now?';
        }
    }

    return 'Are you ready to send this now?';
}

// @ts-expect-error
function determineSequenceLink(status): LocationShape | string | undefined {
    const emailSequence = status && status.emailSequence;

    if (emailSequence) {
        if (emailSequence.nextUnsentEmail) {
            return {
                hash: `/edit-scheduled-email/${emailSequence.nextUnsentEmail.id}`,
            };
        }

        const firstMessage = emailSequence.emailsQueued.length
            ? emailSequence.emailsQueued[0]
            : null;
        if (firstMessage) {
            return firstMessage.htmlUrl;
        }
    }
}

export function AutomatedEmailRow(props: Props) {
    const {emailAutomationStatus, emailAutomation, onStart, onCancel, user, onSendNextUnsentEmail} =
        props;
    const savedTimeoutRef = React.useRef();
    const [cancelButtonRef, setCancelButtonRef] = React.useState<HTMLElement | null | undefined>(
        null
    );
    const [sendQueuedEmailButtonRef, setSendQueuedEmailButtonRef] = React.useState<
        HTMLElement | null | undefined
    >(null);
    // Used to track if the user manually sent the next queued email
    const [isSent, setIsSent] = React.useState<boolean>(false);
    // Used to change UI while an email is sending
    const [isSending, setIsSending] = React.useState<boolean>(false);
    const [showSendQueuedEmailDialog, setShowSendQueuedEmailDialog] =
        React.useState<boolean>(false);

    // -- Title
    const [title, setTitle] = React.useState<string>(
        determineTitle(emailAutomationStatus, emailAutomation)
    );
    React.useEffect(() => {
        setTitle(determineTitle(emailAutomationStatus, emailAutomation));
    }, [emailAutomationStatus, emailAutomation]);

    // -- Descriptions
    const [descriptions, setDescriptions] = React.useState<Description[]>(
        determineDescriptions(
            emailAutomationStatus,
            emailAutomation,
            user ? user.id : null,
            isSent,
            isSending
        )
    );

    React.useEffect(() => {
        setDescriptions(
            determineDescriptions(
                emailAutomationStatus,
                emailAutomation,
                user ? user.id : null,
                isSent,
                isSending
            )
        );
    }, [emailAutomationStatus, emailAutomation, user, isSent, isSending]);
    React.useEffect(() => {
        ReactTooltip.rebuild();
    }, [descriptions]);

    // -- Sequence link
    const [sequenceLink, setSequenceLink] = React.useState<LocationShape | string | undefined>(
        determineSequenceLink(emailAutomationStatus)
    );
    React.useEffect(() => {
        setSequenceLink(determineSequenceLink(emailAutomationStatus));
    }, [emailAutomationStatus]);

    const [saved, setSaved] = React.useState<boolean>(false);
    React.useEffect(
        function () {
            // @ts-expect-error
            const onScheduledEmailSave = (e) => {
                const emailSequenceId = e.detail;

                if (
                    emailAutomationStatus &&
                    emailAutomationStatus.emailSequence &&
                    emailAutomationStatus.emailSequence.id === emailSequenceId
                ) {
                    setSaved(true);
                    if (savedTimeoutRef.current) {
                        clearTimeout(savedTimeoutRef.current);
                    }
                    // @ts-expect-error
                    savedTimeoutRef.current = setTimeout(() => {
                        setSaved(false);
                    }, 5000);
                }
            };

            window.addEventListener('refreshScheduledEmails', onScheduledEmailSave);

            return function cleanup() {
                window.removeEventListener('refreshScheduledEmails', onScheduledEmailSave);
            };
        },
        [emailAutomationStatus]
    );

    // -- Starting
    const handleStart = React.useCallback(() => {
        if (emailAutomationStatus) {
            onStart(emailAutomationStatus.id);
        }
    }, [emailAutomationStatus, onStart]);

    const canStart = emailAutomationStatus && emailAutomationStatus.canStart;

    // -- Cancelling
    const handleCancel = React.useCallback(() => {
        if (emailAutomationStatus) {
            onCancel(emailAutomationStatus.id);
        }
    }, [emailAutomationStatus, onCancel]);
    const canCancel = emailAutomationStatus && emailAutomationStatus.canCancel;

    // -- Sending next email
    const nextUnsentEmail =
        emailAutomationStatus && emailAutomationStatus.emailSequence
            ? emailAutomationStatus.emailSequence.nextUnsentEmail
            : undefined;
    // Only the sender can send the next queued email manually
    const isCurrentUserSender =
        emailAutomationStatus &&
        emailAutomationStatus.sender &&
        user &&
        emailAutomationStatus.sender.id === user.id;

    const handleSendNextQueuedEmail =
        onSendNextUnsentEmail && nextUnsentEmail && isCurrentUserSender
            ? () => {
                  return onSendNextUnsentEmail(nextUnsentEmail.id);
              }
            : undefined;

    const messageCount =
        (emailAutomationStatus &&
            emailAutomationStatus.emailSequence &&
            emailAutomationStatus.emailSequence.messageCount) ||
        (emailAutomation.emailList &&
            // @ts-expect-error
            Array.isArray(emailAutomation.emailList.emailSequenceTemplateMessages) &&
            // @ts-expect-error
            emailAutomation.emailList.emailSequenceTemplateMessages.length) ||
        0;

    const sequenceTemplateCreatorId =
        emailAutomationStatus &&
        emailAutomationStatus.emailSequence &&
        emailAutomationStatus.emailSequence.emailSequenceTemplate
            ? emailAutomationStatus.emailSequence.emailSequenceTemplate.creatorUser.id
            : null;
    const sequenceTemplateId =
        emailAutomationStatus && emailAutomationStatus.emailAutomation.emailList
            ? emailAutomationStatus.emailAutomation.emailList.id
            : null;
    const emailAutomationSenderId =
        emailAutomationStatus && emailAutomationStatus.sender
            ? emailAutomationStatus.sender.id
            : null;

    const isSendingDefaultSequence =
        sequenceTemplateCreatorId &&
        emailAutomationSenderId &&
        sequenceTemplateCreatorId !== emailAutomationSenderId;

    // In theory default sequence could be the current user's, even if they're not the
    // sender, in which case we don't want to ask them to customize their own version
    const currentUserIsSequenceTemplateCreator = user && user.id === sequenceTemplateCreatorId;

    const titleComponent = (
        <SavedSettingToast state={saved ? 'complete' : 'initial'} savedText='Updated'>
            <div className={styles.title}>{title}</div>
        </SavedSettingToast>
    );

    const emailSendNowConfimatonDescription = determineSendNowDescription(emailAutomationStatus);

    return (
        <div className={styles['card-container']}>
            <ActionBox
                size='small'
                title={
                    sequenceLink &&
                    // Make sure that the current user is sending the email
                    // sequence, therefore they can edit it
                    user &&
                    emailAutomationSenderId &&
                    user.id === emailAutomationSenderId ? (
                        // @ts-expect-error
                        <Link as={RoutedLink} to={sequenceLink}>
                            {titleComponent}
                        </Link>
                    ) : (
                        titleComponent
                    )
                }
                subtitle={
                    <div className={isSent ? styles['green-description'] : undefined}>
                        {descriptions.map((line, index) => {
                            let key = index;
                            if (typeof line.description === 'string') {
                                // @ts-expect-error
                                key = line.description;
                            } else if (line.tooltip) {
                                // @ts-expect-error
                                key = line.tooltip;
                            } else if (line.warningTooltip) {
                                // @ts-expect-error
                                key = line.warningTooltip;
                            }

                            return (
                                <div key={key}>
                                    {line.tooltip ? (
                                        <span className={styles['error-icon']}>
                                            <ErrorCircleIcon data-tip={line.tooltip} />
                                        </span>
                                    ) : null}
                                    {line.warningTooltip ? (
                                        <span className={styles['warn-icon']}>
                                            <WarningIcon
                                                data-tip={line.warningTooltip}
                                                // @ts-expect-error
                                                color={colors.yellow}
                                                size={15}
                                            />
                                        </span>
                                    ) : null}
                                    {line.description}
                                </div>
                            );
                        })}
                    </div>
                }
                icon={<EmailSequenceIcon messageCount={messageCount} size={20} />}
                actionElement={
                    <div className='flex'>
                        {shouldShowConnectUserCTALink(props.user, emailAutomationStatus) ? (
                            <Link
                                variant='primary'
                                // @ts-expect-error
                                as={RoutedLink}
                                to={{
                                    hash: '/connect-email/get-started',
                                }}
                            >
                                Connect your email
                            </Link>
                        ) : (
                            <ButtonMover
                                direction={
                                    // Kinda dumb, but if we show the customize
                                    // your template link, it doesnt have
                                    // padding so we need to not remove the
                                    // non-existant padding
                                    isSendingDefaultSequence && sequenceTemplateId ? [] : ['right']
                                }
                                size='normal'
                            >
                                {canStart ? (
                                    <Button variant='text-primary' onClick={handleStart} size='big'>
                                        Start
                                    </Button>
                                ) : null}
                                {canCancel ? (
                                    <Button
                                        variant='text'
                                        size='big'
                                        onClick={() => {
                                            // Set the cancel button ref on the parent
                                            // component. Without this, multiple cancel
                                            // buttons messes with the correct refs
                                            props.setCancelButtonRef(cancelButtonRef);
                                            handleCancel();
                                        }}
                                        // @ts-expect-error
                                        getButtonRef={(node) => {
                                            setCancelButtonRef(node);
                                        }}
                                    >
                                        Cancel email
                                    </Button>
                                ) : null}
                                {handleSendNextQueuedEmail &&
                                !isSent &&
                                !isSendingDefaultSequence ? (
                                    <Button
                                        variant='text-primary'
                                        size='big'
                                        disabled={isSending}
                                        onClick={() => {
                                            setShowSendQueuedEmailDialog(true);
                                        }}
                                        // @ts-expect-error
                                        getButtonRef={(node) => {
                                            setSendQueuedEmailButtonRef(node);
                                        }}
                                    >
                                        {isSending ? 'Sending…' : 'Send now'}
                                    </Button>
                                ) : null}
                                {isSendingDefaultSequence &&
                                sequenceTemplateId &&
                                !currentUserIsSequenceTemplateCreator ? (
                                    <Link
                                        variant='primary'
                                        // @ts-expect-error
                                        as={RoutedLink}
                                        to={`/email/templates#/templates/edit?sequenceId=${sequenceTemplateId}`}
                                    >
                                        Customize your version
                                    </Link>
                                ) : null}
                                {showSendQueuedEmailDialog && handleSendNextQueuedEmail ? (
                                    <Dialog
                                        anchor={sendQueuedEmailButtonRef}
                                        question={emailSendNowConfimatonDescription}
                                        primaryText='Yes, send now'
                                        secondaryText='No, don’t send now'
                                        onPrimary={() => {
                                            setIsSending(true);
                                            setShowSendQueuedEmailDialog(false);
                                            handleSendNextQueuedEmail().then(() => {
                                                setIsSending(false);
                                                setIsSent(true);
                                            });
                                        }}
                                        onSecondary={() => {
                                            setShowSendQueuedEmailDialog(false);
                                        }}
                                    />
                                ) : undefined}
                            </ButtonMover>
                        )}
                    </div>
                }
            />
        </div>
    );
}

// Should we show the current user a "Connect your email" CTA link in this row.
// Only shows up if both:
// 1) The user needs to connect their email
// 2) The automation is configured for them
function shouldShowConnectUserCTALink(
    user?: SessionUserFragment | null,
    emailAutomationStatus?: LeadStagesEdge_emailAutomationStatuses | null
) {
    if (!user || !emailAutomationStatus) return false;
    if (!emailAutomationStatus.sender) return false;
    if (user && user.id !== emailAutomationStatus.sender.id) return false;

    return !user.sendableEmailAddresses.filter((emailAddress) => emailAddress.isEnabledForSending)
        .length;
}
